import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * Hatim ve duyuru sayfalarının yumuşak düzen parçaları (mobildeki gibi):
 * kutusuz, ince çizgiyle ayrılmış tam genişlik satırlar, üstte sabit açık
 * özet şeridi, altta çizgiyle ayrılmış sabit düğme bölgesi. Koyu tema:
 * Tailwind `dark:` (html'de `dark` sınıfı) ve MUI palette.mode.
 */

/** Koyu tema renkleri (mobil DARK ile aynı). */
export const DARK = {
  bg: "#123B3D",
  text: "#FFFFFF",
  muted: "rgba(255,255,255,0.7)",
  faint: "rgba(255,255,255,0.45)",
  line: "rgba(255,255,255,0.14)",
  chip: "rgba(255,255,255,0.1)",
  accent: "#8FC9CA",
  success: "#7BE3A8",
  danger: "#F2A59C",
};

export const ACTION_GREEN = { light: "#2E9E6A", dark: "#34B37A" };

export function useIsDark() {
  const theme = useTheme();
  return theme?.palette?.mode === "dark";
}

/** İlerleme rengi: koyuda koyu zeminde okunan tonlar. */
export function progressColor(pct, dark) {
  if (pct <= 0) return dark ? DARK.danger : "#C0392B";
  if (pct < 50) return "#E8A23A";
  if (pct < 75) return dark ? DARK.success : "#2E9E6A";
  return dark ? DARK.accent : "#006A71";
}

/** Ortak sınıflar (açık + koyu). */
export const cls = {
  panel:
    "bg-terakkiSurface border border-terakkiBorder rounded-lg dark:bg-white/[0.03] dark:border-white/[0.14]",
  summary:
    "bg-terakkiBrandCloud border-b border-terakkiBorder dark:bg-white/[0.06] dark:border-white/[0.14]",
  row: "border-b border-terakkiBorder dark:border-white/[0.14]",
  rowHover: "hover:bg-terakkiBrandCloud dark:hover:bg-white/[0.06]",
  title: "text-terakkiInk dark:text-white",
  heading: "text-terakkiPrimaryDark dark:text-white",
  muted: "text-terakkiNeutral dark:text-white/70",
  faint: "text-terakkiNeutral/80 dark:text-white/45",
  accent: "text-terakkiPrimaryDark dark:text-[#8FC9CA]",
  avatar:
    "bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/10 dark:text-[#8FC9CA]",
  chip: "bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/10 dark:text-[#8FC9CA]",
  track: "bg-terakkiBrandMist dark:bg-white/10",
  skeleton: "bg-terakkiBrandMist dark:bg-white/[0.12] animate-pulse",
  footer: "border-t border-terakkiBorder dark:border-white/[0.14]",
  danger: "text-terakkiError dark:text-[#F2A59C]",
  warning: "text-terakkiWarning",
  input:
    "dark:bg-white/10 dark:text-white dark:border-white/[0.14] dark:placeholder:text-white/45",
};

/** Başlık yanındaki küçük yumuşak etiket (ör. "Kurucu"). */
export function SoftTag({ children }) {
  return (
    <span className={`shrink-0 text-[11px] font-SemiBold px-2 py-0.5 rounded-full ${cls.chip}`}>
      {children}
    </span>
  );
}

/** Tam genişlik, ince alt çizgili satır: baş harf/simge, ad, alt bilgi, sağ uç. */
export function SoftRow({ avatar, title, titleAccessory, meta, right, children, className = "" }) {
  return (
    <li className={`flex flex-col gap-2 px-4 py-3 ${cls.row} ${className}`}>
      <div className="flex items-center gap-3">
        {avatar ? (
          <span
            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-SemiBold ${cls.avatar}`}
          >
            {avatar}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`text-base font-SemiBold truncate ${cls.title}`}>{title}</span>
            {titleAccessory}
          </div>
          {meta ? <div className={`text-xs truncate mt-0.5 ${cls.muted}`}>{meta}</div> : null}
        </div>
        {right}
      </div>
      {children}
    </li>
  );
}

/** Satır iskeleti: gerçek satırla aynı yükseklikte. */
export function SoftRowSkeleton({ rows = 4, avatar = true }) {
  return (
    <ul>
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className={`flex items-center gap-3 px-4 py-3 ${cls.row}`}>
          {avatar ? <span className={`w-9 h-9 rounded-full shrink-0 ${cls.skeleton}`} /> : null}
          <div className="flex-1 flex flex-col gap-1.5">
            <span className={`h-4 w-1/2 rounded ${cls.skeleton}`} />
            <span className={`h-3 w-1/3 rounded ${cls.skeleton}`} />
          </div>
          <span className={`h-4 w-12 rounded ${cls.skeleton}`} />
        </li>
      ))}
    </ul>
  );
}

/** İnce ilerleme çubuğu. */
export function SoftBar({ pct, color, height = 4 }) {
  return (
    <div className={`w-full rounded-full overflow-hidden ${cls.track}`} style={{ height }}>
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: color }}
      />
    </div>
  );
}

/** Panelin altında çizgiyle ayrılmış sabit düğme bölgesi. */
export function SoftFooter({ children, className = "" }) {
  return <div className={`shrink-0 px-4 py-3 flex gap-2 ${cls.footer} ${className}`}>{children}</div>;
}

const tones = {
  // başlatan (Ekle, Oluştur, İleri): turkuaz
  primary: (dark) => ({
    backgroundColor: dark ? "#FFFFFF" : "#006A71",
    color: dark ? "#006A71" : "#FFFFFF",
    "&:hover": { backgroundColor: dark ? "#E6F4F4" : "#00565C" },
  }),
  // kaydet / tamamla: yeşil
  success: (dark) => ({
    backgroundColor: dark ? ACTION_GREEN.dark : ACTION_GREEN.light,
    color: "#FFFFFF",
    "&:hover": { backgroundColor: dark ? "#2E9E6A" : "#26885B" },
  }),
  // iptal / vazgeç: yumuşak kırmızı
  cancel: (dark) => ({
    backgroundColor: dark ? "rgba(242,165,156,0.14)" : "rgba(192,57,43,0.08)",
    color: dark ? DARK.danger : "#C0392B",
    "&:hover": { backgroundColor: dark ? "rgba(242,165,156,0.22)" : "rgba(192,57,43,0.14)" },
  }),
  // sil / çıkar: dolu kırmızı
  danger: () => ({
    backgroundColor: "#C0392B",
    color: "#FFFFFF",
    "&:hover": { backgroundColor: "#A93226" },
  }),
  // nötr ikincil (kopyala, düzenle)
  soft: (dark) => ({
    backgroundColor: dark ? DARK.chip : "#F1F8F8",
    color: dark ? DARK.accent : "#006A71",
    "&:hover": { backgroundColor: dark ? "rgba(255,255,255,0.16)" : "#D6EBEB" },
  }),
};

/** Tonlu, yuvarlak düğme. tone: primary | success | cancel | danger | soft */
export function SoftButton({ tone = "primary", sx, fullWidth, ...rest }) {
  const dark = useIsDark();
  return (
    <Button
      disableElevation
      fullWidth={fullWidth}
      {...rest}
      sx={{
        borderRadius: 999,
        px: 2.25,
        py: 0.9,
        fontWeight: 600,
        textTransform: "none",
        ...tones[tone](dark),
        "&.Mui-disabled": { opacity: 0.45, ...tones[tone](dark), color: tones[tone](dark).color },
        ...sx,
      }}
    />
  );
}

/** Koyu temada koyu kart olan pencere kağıdı (MUI Dialog PaperProps.sx). */
export function useDialogPaperSx() {
  const dark = useIsDark();
  return dark
    ? { backgroundColor: DARK.bg, backgroundImage: "none", color: DARK.text, border: `1px solid ${DARK.line}` }
    : {};
}
